import { useEffect, useRef, useState } from "react";
import type { GoalsResponse, RequestStatus } from "@howcomet/shared";
import { waitingCopy } from "@howcomet/shared";
import { api, ApiError } from "../api";
import { BigButton } from "../components/BigButton";
import { Approved } from "../components/Approved";

/**
 * The kid picks the goal from the presets a grown-up priced. One active goal at
 * a time, shown as a bar with a plain-language pace estimate.
 *
 * Switching is free and expected — a six-year-old will switch constantly, and
 * that is fine, because the mechanic is the visible gap, not the commitment.
 *
 * When the bar fills, this sends a request. The app never transacts.
 */
/**
 * Every status a past trade can be in, and what the child reads.
 *
 * A `Record` keyed on the union rather than a chain of ternaries, so adding a
 * status is a compile error here instead of a wrong word on a tablet. It was a
 * ternary, and `delivered` — a promise the child had confirmed themselves —
 * fell through the else branch and came back as "Not yet".
 */
const HISTORY: Record<RequestStatus, { label: string; tone: string }> = {
  pending: { label: "Waiting", tone: "text-cyan" },
  approved: { label: "On its way", tone: "text-cyan" },
  delivered: { label: "Got it 🎉", tone: "text-mint" },
  returned: { label: "Took my ⭐ back", tone: "text-cream/45" },
  denied: { label: "Not yet", tone: "text-cream/45" },
};

/** The statuses that belong in "what happened before". */
const SETTLED: RequestStatus[] = ["delivered", "returned", "denied"];

export function GoalScreen({
  active,
  onAwarded,
}: {
  /** True while this tab is showing. The screen stays mounted either way. */
  active: boolean;
  onAwarded: (message: string) => void;
}) {
  const [goals, setGoals] = useState<GoalsResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  /** The approval being celebrated right now, held so the news survives a reload. */
  const [celebrating, setCelebrating] = useState<GoalsResponse["justApproved"]>(null);
  const seen = useRef<string | null>(null);

  /*
    The child confirming it turned up is what actually closes a trade. Until
    this existed, approval spent the stardust and the app never asked again —
    so a promise nobody kept looked exactly like one that was kept, and the
    child had paid either way.
  */
  const confirmArrived = async (id: string) => {
    setBusy(true);
    try {
      await api.goalArrived(id);
      onAwarded("Lovely. That one's done.");
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save that.");
    } finally {
      setBusy(false);
    }
  };

  /** Cancels the promise and gives the stardust back. */
  const takeItBack = async (id: string) => {
    setBusy(true);
    try {
      const { returned } = await api.goalReturned(id);
      onAwarded(`Your ⭐ ${returned} is back. You can save it for something else.`);
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't do that.");
    } finally {
      setBusy(false);
    }
  };

  const load = () => {
    api
      .goals()
      .then((next) => {
        setGoals(next);
        // Guard on the ref rather than on state: a poll can land while the
        // celebration is still on screen, and re-triggering would restart it.
        if (next.justApproved && seen.current !== next.justApproved.id) {
          seen.current = next.justApproved.id;
          setCelebrating(next.justApproved);
        }
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "Couldn't load your goals."));
  };

  // Opened, not mounted — stardust earned in the chat has to show up here.
  useEffect(() => {
    if (active) load();
  }, [active]);

  /*
    A grown-up approves on their own phone, minutes or hours later, and there is
    no live connection to this tablet — so without this the screen sat on
    "waiting" until a hard refresh, and the child found out by being told.

    Polling only while something is actually pending, so a child reading their
    goals costs nothing. Ten seconds is slow enough to be invisible on battery
    and fast enough that a child still holding the tablet sees it happen.
  */
  useEffect(() => {
    // Only while the child is actually looking. A hidden tab polling every ten
    // seconds is battery spent on a screen nobody can see; opening the tab
    // reloads anyway, so no approval is missed.
    if (!active || !goals?.pendingRequest) return;
    const timer = window.setInterval(load, 10_000);
    return () => window.clearInterval(timer);
  }, [active, goals?.pendingRequest?.id]);

  const choose = async (rewardId: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.setGoal(rewardId);
      load();
    } finally {
      setBusy(false);
    }
  };

  const askGrownUp = async (rewardId: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.requestGoal(rewardId);
      onAwarded("Sent. A grown-up will see it.");
      load();
    } catch (e) {
      onAwarded(e instanceof ApiError ? e.message : "Couldn't send that.");
    } finally {
      setBusy(false);
    }
  };

  const stopCelebrating = () => {
    const done = celebrating;
    setCelebrating(null);
    if (done) void api.celebrated(done.id).then(load).catch(() => {});
  };

  if (error) return <p className="rise text-cream/70">{error}</p>;
  if (!goals) return <p className="rise text-cream/60">Loading…</p>;

  const activeReward = goals.rewards.find((r) => r.id === goals.activeRewardId);

  const celebration = celebrating && (
    <Approved label={celebrating.label} emoji={celebrating.emoji} onDone={stopCelebrating} />
  );

  return (
    <>
      {celebration}
    <div className="rise pb-32">
      <p className="disp text-[30px] text-cream">My goal</p>
      <p className="mb-5 mt-1 text-cream/60">
        You have ⭐ {goals.stardust}. Pick what you're saving for — you can change it any time.
      </p>

      {/*
        A promise a grown-up made that hasn't arrived. Sits above the goal
        because it is the more important thing on this screen: the child's
        stardust is inside it.

        Both answers are offered plainly and neither is framed as the right one.
        "It never came" is not a complaint a child should have to feel brave
        about — it is just true sometimes, and the app should be able to hear it.
      */}
      {goals.awaitingDelivery && (
        <div className="pop mb-6 rounded-3xl border-2 border-cyan/40 bg-panel2 p-5">
          <div className="flex items-center gap-3">
            <span className="text-[40px]">{goals.awaitingDelivery.emoji}</span>
            <div className="min-w-0 flex-1">
              <p className="disp text-[22px] text-cream">{goals.awaitingDelivery.label}</p>
              <p className="mt-1 text-[15px] text-cyan">
                {waitingCopy(goals.awaitingDelivery.waitingDays)}
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-3">
            <BigButton
              color="mint"
              disabled={busy}
              onClick={() => void confirmArrived(goals.awaitingDelivery!.id)}
            >
              I got it!
            </BigButton>
            <button
              disabled={busy}
              onClick={() => void takeItBack(goals.awaitingDelivery!.id)}
              className="kidbtn rounded-2xl py-2 text-[15px] text-cream/55"
            >
              It never came — take my ⭐ {goals.awaitingDelivery.cost} back
            </button>
          </div>
        </div>
      )}

      {activeReward && (
        <div className="pop mb-6 rounded-3xl bg-panel2 p-5">
          <div className="flex items-center gap-3">
            <span className="text-[40px]">{activeReward.emoji}</span>
            <div className="min-w-0 flex-1">
              <p className="disp text-[22px] text-cream">{activeReward.label}</p>
              <ProgressBar value={goals.stardust} max={activeReward.cost} />
              <p className="mt-2 text-[14px] text-cream/70">
                {goals.stardust >= activeReward.cost
                  ? "Ready to trade!"
                  : `${activeReward.cost - goals.stardust} more to go · ⭐ ${activeReward.cost}`}
              </p>
              {goals.paceMessage && (
                <p className="mt-1 text-[14px] text-cyan">{goals.paceMessage}</p>
              )}
            </div>
          </div>

          <div className="mt-4">
            {goals.pendingRequest ? (
              <div className="rounded-2xl bg-cream/8 py-3 text-center font-extrabold text-cyan">
                Waiting for a grown-up…
              </div>
            ) : (
              <BigButton
                color="mint"
                disabled={goals.stardust < activeReward.cost || busy}
                onClick={() => void askGrownUp(activeReward.id)}
              >
                {goals.stardust >= activeReward.cost ? "Ask a grown-up" : "Keep learning"}
              </BigButton>
            )}
          </div>
        </div>
      )}

      {/*
        What the child sees their grown-up called, and the only place a title
        appears. Small, warm, and never comparative — there is no other family
        on this screen and there is not going to be one.
      */}
      {goals.parentRecord.title && (
        <div className="mb-6 rounded-3xl bg-panel/60 px-5 py-4">
          <p className="disp text-[17px] text-amber">
            🏅 Your grown-up: {goals.parentRecord.title}
          </p>
          <p className="mt-1 text-[14px] text-cream/55">
            {goals.parentRecord.kept}{" "}
            {goals.parentRecord.kept === 1 ? "promise kept" : "promises kept"}
            {goals.parentRecord.typicalDays !== null &&
              (goals.parentRecord.typicalDays < 1
                ? ", usually the same day"
                : goals.parentRecord.typicalDays <= 1.5
                  ? ", usually within a day"
                  : `, usually in about ${Math.round(goals.parentRecord.typicalDays)} days`)}
            .
          </p>
        </div>
      )}

      <p className="disp mb-3 text-[13px] uppercase tracking-wider text-cream/45">
        {activeReward ? "Or save for something else" : "Pick a goal"}
      </p>

      <div className="flex flex-col gap-3">
        {goals.rewards
          .filter((reward) => reward.id !== goals.activeRewardId)
          .map((reward) => {
            const percent = Math.min(100, Math.round((goals.stardust / reward.cost) * 100));
            return (
              <button
                key={reward.id}
                disabled={busy}
                onClick={() => void choose(reward.id)}
                className="kidbtn flex items-center gap-3 rounded-3xl bg-panel p-4 text-left"
              >
                <span className="text-[32px]">{reward.emoji}</span>
                <div className="min-w-0 flex-1">
                  <p className="disp text-[19px] text-cream">{reward.label}</p>
                  <ProgressBar value={goals.stardust} max={reward.cost} />
                  <p className="mt-1 text-[13px] text-cream/55">
                    ⭐ {reward.cost} · {percent}%
                  </p>
                </div>
              </button>
            );
          })}
      </div>

      <SuggestGoal goals={goals} onSent={load} onError={onAwarded} />

      {/* Settled trades only. A promise still on its way has its own card at
          the top of this screen, and listing it twice made the history read
          like a second, quieter version of the same thing. */}
      {goals.recentRequests.some((r) => SETTLED.includes(r.status)) && (
        <div className="mt-6">
          <p className="disp mb-2 text-[13px] uppercase tracking-wider text-cream/45">
            What happened before
          </p>
          {goals.recentRequests
            .filter((r) => SETTLED.includes(r.status))
            .slice(0, 4)
            .map((request) => {
              const reward = goals.rewards.find((r) => r.id === request.rewardId);
              return (
                <div key={request.id} className="flex items-center gap-2 py-1.5 text-[15px]">
                  <span>{reward?.emoji ?? "🎁"}</span>
                  <span className="flex-1 text-cream/70">{reward?.label ?? "A goal"}</span>
                  <span className={`font-bold ${HISTORY[request.status].tone}`}>
                    {HISTORY[request.status].label}
                  </span>
                </div>
              );
            })}
        </div>
      )}
    </div>
    </>
  );
}

/**
 * Where a kid says what they want. There is deliberately no price field: the
 * child names the thing, a grown-up names the number. That split is what keeps
 * the goal list from becoming a wish granted on demand — and it's also why the
 * copy says "ask", not "add".
 */
function SuggestGoal({
  goals,
  onSent,
  onError,
}: {
  goals: GoalsResponse;
  onSent: () => void;
  onError: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  if (goals.suggestion) {
    return (
      <div className="mt-6 rounded-3xl border-2 border-cyan/40 bg-panel p-4">
        <p className="disp text-[15px] uppercase tracking-wider text-cyan">Your idea</p>
        <p className="mt-1 text-[19px] text-cream">“{goals.suggestion.text}”</p>
        <p className="mt-2 text-[15px] text-cream/60">
          A grown-up is deciding how much stardust it's worth. It'll show up here when they do.
        </p>
      </div>
    );
  }

  const send = async () => {
    if (!text.trim() || busy) return;
    setBusy(true);
    try {
      await api.suggestGoal(text.trim());
      setText("");
      setOpen(false);
      onSent();
      onError("Sent. A grown-up will pick the price.");
    } catch (e) {
      onError(e instanceof ApiError ? e.message : "Couldn't send that.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="kidbtn mt-6 w-full rounded-3xl border-2 border-dashed border-cream/25 p-4 text-left"
      >
        <p className="disp text-[19px] text-cream">Want something that isn't here?</p>
        <p className="mt-1 text-[15px] text-cream/55">
          Tell a grown-up what to add. They decide how much stardust it costs.
        </p>
      </button>
    );
  }

  return (
    <div className="pop mt-6 rounded-3xl bg-panel p-4">
      <p className="disp text-[19px] text-cream">What do you want to save for?</p>
      <input
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, 60))}
        onKeyDown={(e) => e.key === "Enter" && void send()}
        placeholder="A trip to the zoo"
        autoFocus
        className="mt-3 w-full rounded-2xl bg-panel2 px-4 py-3 text-[18px] text-cream outline-none placeholder:text-cream/35"
      />
      <p className="mt-2 text-[14px] text-cream/50">
        A grown-up decides how much stardust it costs, then it appears in your list.
      </p>
      <div className="mt-3 flex gap-2">
        <button
          onClick={() => void send()}
          disabled={!text.trim() || busy}
          className="kidbtn disp rounded-full bg-gold px-5 py-2 text-night disabled:bg-cream/15 disabled:text-cream/40"
        >
          {busy ? "Sending…" : "Ask a grown-up"}
        </button>
        <button
          onClick={() => {
            setOpen(false);
            setText("");
          }}
          className="kidbtn rounded-full bg-cream/12 px-5 py-2 font-bold text-cream"
        >
          Never mind
        </button>
      </div>
    </div>
  );
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const percent = Math.min(100, Math.round((value / max) * 100));
  const full = value >= max;
  return (
    <div
      className="mt-2 h-2.5 overflow-hidden rounded-full bg-cream/12"
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={`h-full transition-[width] duration-500 ${full ? "bg-mint" : "bg-gold"}`}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}
